import { CIStatusCapsule, CITableGrid } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIG_CONST } from './StatusConstant';
import classes from './SummaryGrid.module.scss';
import { capitalizeWords, splitCamelCase } from './Utils';

interface ConfigDetailType {
  status: string,
  recordName: string,
  errorMessage: string,
  srcRecordVersion: number,
  targetRecordVersion?: number,
}

interface GridProps {
  gridData: ConfigDetailType[],
  configType: string
}

enum Labels {
  CONFIG_TYPE = 'recordType',
  CONFIG_NAME = 'recordName',
  SRC_CONFIG_VERSION = 'srcRecordVersion',
  TARGET_CONFIG_VERSION = 'targetRecordVersion',
  ERROR_MESSAGE = 'errorMessage',
  STATUS = 'status'
}

const SummaryGrid = ({ gridData, configType: configtype }: GridProps) => {
  const { t: translate } = useTranslation();

  const [configList, setConfigList] = useState<GridProps | any>(gridData);
  const [page, setPage] = useState<number>(0);
  const [tableHeaders, setTableHeaders] = useState([{
    name: Labels.CONFIG_NAME,
    label: translate('import_summary.config_name'),
    columnData: {
      field: Labels.CONFIG_NAME
    }
  }]);

  /**
  * @method fieldDetails
  * @description Render Field Details for config name and error message column
  *  @param {string} value - config name or error message
  */
  const fieldDetails = (value: string) => (
    <div className={classes['field-details']} title={value}>
      <p className={classes['field-details-text']}>{value}
      </p>
    </div>
  );

  /**
  * @method renderConfigName
  * @description Render Config name
  * @param {ConfigDetailType} row - config details.
  */
  const renderConfigName = (row: ConfigDetailType) => {
    const value = row[Labels.CONFIG_NAME];
    return fieldDetails(value);
  };

  /**
  * @method renderErrorMessage
  * @description Render Error message
  * @param {ConfigDetailType} row - config details.
  */
  const renderErrorMessage = (row: ConfigDetailType) => {
    const value = row[Labels.ERROR_MESSAGE];
    return fieldDetails(value);
  };

  /**
  * @method renderSummaryStatus
  * @description Render Summary Status
  * @param {ConfigDetailType} row - config details.
  */
  const renderSummaryStatus = (row: ConfigDetailType) => (
    <div>
      <CIStatusCapsule status={row.status} />
    </div>
  );

  /**
  * @method setGridTableHeaders
  * @description Sets grid header configuration based on configtype
  * @param {string} configtype - config type.
  */
  const setGridTableHeaders = (configtype: string) => {
    const configHeaderLabel = configtype ? `${capitalizeWords(splitCamelCase(configtype))} Name` : translate('import_summary.config_name');

    let headers = [
    ];

    const configNameHeader = [{
      name: Labels.CONFIG_NAME,
      label: configHeaderLabel,
      options: { breakPointWidth: 3 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.CONFIG_NAME, render: renderConfigName
      }
    }];

    const configStatusHeader = [{
      name: Labels.STATUS,
      label: translate('import_summary.status'),
      options: { breakPointWidth: 1 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.STATUS, render: renderSummaryStatus
      }
    }];

    const versionConfigsHeader = [{
      name: Labels.SRC_CONFIG_VERSION,
      label: translate('import_summary.source_version'),
      options: { breakPointWidth: 2 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.SRC_CONFIG_VERSION
      }
    },
    {
      name: Labels.TARGET_CONFIG_VERSION,
      label: translate('import_summary.target_version'),
      options: { breakPointWidth: 2 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.TARGET_CONFIG_VERSION
      }
    }];

    const errorMsgHeader = [{
      name: Labels.ERROR_MESSAGE,
      label: translate('import_summary.error_message'),
      options: { breakPointWidth: configtype?.toLocaleLowerCase() !== CONFIG_CONST.SCHEMA ? 8 : 4 },
      labelOptions: { helperText: '', helperTextClass: '' },
      columnData: {
        field: Labels.ERROR_MESSAGE, render: renderErrorMessage
      }
    }];

    if (configtype?.toLocaleLowerCase() !== CONFIG_CONST.SCHEMA) {
      headers = [...configNameHeader, ...configStatusHeader, ...errorMsgHeader];
    } else {
      headers = [...configNameHeader, ...configStatusHeader, ...versionConfigsHeader, ...errorMsgHeader];
    }

    setTableHeaders(headers);
  };

  /**
  * @method onPageNoUpdate
  * @description Sets page for pagination
  * @param {number} pageNumber - Page Number.
  */
  const onPageNoUpdate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const footerOptionsObj = {
    selectItems: [5],
    itemsPerPage: 5,
    currentPageNumber: page,
    numberOfPages: Math.ceil(gridData.length / 5),
    onCurrentPageNumberChange: onPageNoUpdate,
    onItemsPerPageChange: () => { },
    key: page + 1
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  // For handling pagination
  useEffect(() => {
    if ((page + 1) > Math.ceil(gridData.length / 5) && page) {
      onPageNoUpdate(Math.ceil(gridData.length / 5) - 1);
    }
    const list = gridData.filter((_exception, index) => (page * 5) <= index && index < (page * 5) + 5);
    setConfigList(list);
  }, [page, gridData]);

  // sets header configuration on mount
  useEffect(() => {
    setGridTableHeaders(configtype);
  }, []);

  return (
    <CITableGrid
      headers={tableHeaders}
      sorted={{ sortedName: '', sortedOrder: '' }}
      footer={footerObj}
      tableData={configList}
    />
  );
};

export default SummaryGrid;
